import { Button, Checkbox, FormField, SegmentedControl } from "@playerelite/ui";
import type { SegmentedControlOption } from "@playerelite/ui";
import { AttractorContentType } from "../types";
import PromotionSearchSelect from "./PromotionSearchSelect";
import {
  AttractorContentValue,
  gamingCheckboxState,
  GamingCheckboxHint,
  isLinkable,
  linkNoun,
  withContentType,
  withLinkedPromotion,
} from "./attractor-content";

const CONTENT_TYPE_OPTIONS: SegmentedControlOption[] = [
  { value: "General", label: "General", icon: "chat" },
  { value: "Game", label: "Game", icon: "gamepad" },
  { value: "Promotion", label: "Promotion", icon: "tag" },
];

const GAMING_HINTS: Record<GamingCheckboxHint, string> = {
  general: "Only show this attractor on gaming units.",
  "game-auto": "Games are always gaming content.",
  "promotion-auto":
    "Detected from the linked promotion when you save. Tick or untick to set it yourself.",
  // Also shown for a saved value, which may have been detected rather than chosen, so it
  // describes what happens next rather than who set it.
  manual: "Kept as shown when you save, instead of being detected.",
};

interface Props {
  venueId: number | undefined;
  value: AttractorContentValue;
  onChange: (value: AttractorContentValue) => void;
  /** Shown under the linked game / promotion field. */
  linkError?: string;
  disabled?: boolean;
}

/**
 * Content type, the game or promotion it links to, and the "gaming content only" flag.
 * Shared by the kiosk and mobile attractor forms.
 */
const AttractorContentFields = ({
  venueId,
  value,
  onChange,
  linkError,
  disabled,
}: Props) => {
  const gaming = gamingCheckboxState(value);
  const { contentType } = value;

  return (
    <>
      <FormField label="Content type" disabled={disabled}>
        <SegmentedControl
          options={CONTENT_TYPE_OPTIONS}
          // Wraps instead of overflowing a narrow dialog.
          className="flex-wrap"
          value={contentType}
          disabled={disabled}
          onValueChange={(next) =>
            onChange(withContentType(value, next as AttractorContentType))
          }
        />
      </FormField>

      {isLinkable(contentType) ? (
        <FormField
          label={`Link to ${linkNoun(contentType)}`}
          error={linkError}
          required
          disabled={disabled}
        >
          <PromotionSearchSelect
            // Remount per type so a half-typed search does not carry across.
            key={contentType}
            venueId={venueId}
            contentType={contentType}
            value={value.linkedPromotion}
            disabled={disabled}
            onChange={(linked) => onChange(withLinkedPromotion(value, linked))}
          />
        </FormField>
      ) : null}

      <div className="flex flex-col items-start gap-1">
        <Checkbox
          label="Gaming content only"
          description={GAMING_HINTS[gaming.hint]}
          checked={gaming.checked}
          disabled={disabled}
          onCheckedChange={(checked) =>
            onChange({ ...value, gamingOverride: checked === true })
          }
        />
        {gaming.canReset ? (
          <Button
            variant="link"
            size="sm"
            className="ml-6"
            disabled={disabled}
            onClick={() => onChange({ ...value, gamingOverride: null })}
          >
            Use automatic detection
          </Button>
        ) : null}
      </div>
    </>
  );
};

export default AttractorContentFields;
